import { Controller } from "@hotwired/stimulus";
import { appendModelingControlParams } from "../scenario_modeling/request_params";

const DISPLAY_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default class extends Controller {
  static targets = [
    "backdrop",
    "panel",
    "title",
    "tableHead",
    "tableBody",
    "emptyState",
    "toggleButton",
    "toggleLabel",
    "blockable",
  ];

  static values = {
    tableUrl: String,
    showLabel: { type: String, default: "Show rates" },
    hideLabel: { type: String, default: "Hide rates" },
  };

  connect() {
    this._dragState = null;
    this._onPointerMove = this._handlePointerMove.bind(this);
    this._onPointerUp = this._handlePointerUp.bind(this);
    this._onKeydown = this._handleKeydown.bind(this);
    this._onDateChange = () => {
      if (this._isOpen()) this._loadTable();
    };

    window.addEventListener("scenario-modeling:exchange-rate-date-change", this._onDateChange);
  }

  disconnect() {
    window.removeEventListener("scenario-modeling:exchange-rate-date-change", this._onDateChange);
    this._endDrag();
    document.removeEventListener("keydown", this._onKeydown);
    this._unlockPage();
  }

  toggle(event) {
    event.preventDefault();
    if (!this._isOpen()) this.open();
  }

  open() {
    this.backdropTarget.classList.remove("hidden");
    this.backdropTarget.setAttribute("aria-hidden", "false");
    this._lockPage();
    this._setToggleLabel(true);
    this._positionPanelDefault();
    this._loadTable();
    document.addEventListener("keydown", this._onKeydown);
  }

  close() {
    this.backdropTarget.classList.add("hidden");
    this.backdropTarget.setAttribute("aria-hidden", "true");
    this._unlockPage();
    this._setToggleLabel(false);
    this._endDrag();
    document.removeEventListener("keydown", this._onKeydown);
  }

  backdropClose(event) {
    if (this.panelTarget.contains(event.target)) return;
    this.close();
  }

  startDrag(event) {
    if (event.button !== 0) return;

    event.preventDefault();
    const rect = this.panelTarget.getBoundingClientRect();

    this._dragState = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    this.panelTarget.setPointerCapture(event.pointerId);
    this.panelTarget.addEventListener("pointermove", this._onPointerMove);
    this.panelTarget.addEventListener("pointerup", this._onPointerUp);
    this.panelTarget.addEventListener("pointercancel", this._onPointerUp);
  }

  async _loadTable() {
    if (!this.tableUrlValue) return;

    this._showLoading();

    try {
      const url = new URL(this.tableUrlValue, window.location.origin);
      const params = new URLSearchParams();
      appendModelingControlParams(params);
      url.search = params.toString();

      const response = await fetch(url, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) throw new Error("exchange rates table request failed");

      const payload = await response.json();
      this._renderTable(payload);
    } catch (_error) {
      this._renderError();
    }
  }

  _renderTable(payload) {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Exchange rates as of ${payload.exchange_rate_date_display}`;
    }

    const targets = payload.target_currencies || [];
    if (this.hasTableHeadTarget) {
      this.tableHeadTarget.innerHTML = `
        <th scope="col" class="px-4 py-3 text-left text-xs font-normal text-[#9CA3AF]">Currency</th>
        ${targets
          .map(
            (currency) =>
              `<th scope="col" class="px-4 py-3 text-right text-xs font-normal text-[#9CA3AF]">${currency}</th>`
          )
          .join("")}
      `;
    }

    const rows = payload.rows || [];
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", rows.length > 0);
    }

    if (this.hasTableBodyTarget) {
      this.tableBodyTarget.innerHTML = rows
        .map((row) => {
          const cells = targets
            .map((currency) => {
              const value = row.rates?.[currency];
              const display = value == null ? "—" : this._formatRate(value);
              return `<td class="px-4 py-3 text-right text-sm tabular-nums text-[#E5E7EB]">${display}</td>`;
            })
            .join("");

          return `
            <tr class="border-t border-[#374151]">
              <td class="px-4 py-3 text-sm font-semibold text-white">${row.currency}</td>
              ${cells}
            </tr>
          `;
        })
        .join("");
    }
  }

  _renderError() {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = "Exchange rates unavailable";
    }
    if (this.hasTableBodyTarget) {
      this.tableBodyTarget.innerHTML = "";
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden");
      this.emptyStateTarget.textContent = "Unable to load exchange rates. Please try again.";
    }
  }

  _showLoading() {
    if (this.hasTitleTarget) {
      const exchangeDate = document.getElementById("scenario_exchange_rate_date")?.value;
      const formatted = this._formatDisplayDate(exchangeDate);
      this.titleTarget.textContent = formatted
        ? `Exchange rates as of ${formatted}`
        : "Exchange rates";
    }
    if (this.hasTableBodyTarget) {
      this.tableBodyTarget.innerHTML = "";
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden");
      this.emptyStateTarget.textContent = "Loading exchange rates…";
    }
  }

  _formatRate(value) {
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 5,
    });
  }

  _formatDisplayDate(isoValue) {
    if (!isoValue) return null;

    const [year, month, day] = isoValue.split("-").map((part) => Number(part));
    const monthLabel = DISPLAY_MONTHS[month - 1];
    if (!monthLabel) return null;

    return `${String(day).padStart(2, "0")} ${monthLabel} ${year}`;
  }

  _positionPanelDefault() {
    const panel = this.panelTarget;
    panel.style.left = "";
    panel.style.top = "";
    panel.style.right = "1.5rem";
    panel.style.transform = "translateY(0)";
  }

  _handlePointerMove(event) {
    if (!this._dragState || event.pointerId !== this._dragState.pointerId) return;

    const panel = this.panelTarget;
    const maxLeft = window.innerWidth - panel.offsetWidth - 8;
    const maxTop = window.innerHeight - panel.offsetHeight - 8;

    const left = Math.min(Math.max(event.clientX - this._dragState.offsetX, 8), maxLeft);
    const top = Math.min(Math.max(event.clientY - this._dragState.offsetY, 8), maxTop);

    panel.style.right = "auto";
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }

  _handlePointerUp(event) {
    if (!this._dragState || event.pointerId !== this._dragState.pointerId) return;
    this._endDrag();
  }

  _endDrag() {
    if (!this._dragState) return;

    this.panelTarget.removeEventListener("pointermove", this._onPointerMove);
    this.panelTarget.removeEventListener("pointerup", this._onPointerUp);
    this.panelTarget.removeEventListener("pointercancel", this._onPointerUp);

    try {
      this.panelTarget.releasePointerCapture(this._dragState.pointerId);
    } catch (_error) {
      // Ignore if capture was already released.
    }

    this._dragState = null;
  }

  _handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    }
  }

  _isOpen() {
    return !this.backdropTarget.classList.contains("hidden");
  }

  _lockPage() {
    document.body.classList.add("overflow-hidden");
    if (this.hasBlockableTarget) {
      this.blockableTarget.inert = true;
    }
  }

  _unlockPage() {
    document.body.classList.remove("overflow-hidden");
    if (this.hasBlockableTarget) {
      this.blockableTarget.inert = false;
    }
  }

  _setToggleLabel(open) {
    if (this.hasToggleLabelTarget) {
      this.toggleLabelTarget.textContent = open ? this.hideLabelValue : this.showLabelValue;
    }
    if (this.hasToggleButtonTarget) {
      const button = this.toggleButtonTarget;
      button.disabled = open;
      button.setAttribute("aria-disabled", open ? "true" : "false");
      button.setAttribute("aria-expanded", open ? "true" : "false");
      button.classList.toggle("cursor-not-allowed", open);
      button.classList.toggle("opacity-60", open);
    }
  }
}
